import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, Subscription } from 'rxjs';
import { BusinessPartner } from '../../model/business_partner';
import { BusinessPartnerProviderLink, Provider } from '../../model/provider';
import { CopilotApiService } from '../../services/copilot-api-service';

@Injectable({
  providedIn: 'root'
})
export class BusinessPartnersService {
  public readonly partners$ = new BehaviorSubject<BusinessPartner[]>([]);
  public readonly partnersLoading$ = new BehaviorSubject<boolean>(false);
  public readonly partnersError$ = new BehaviorSubject<string | null>(null);
  public readonly loading$ = this.partnersLoading$;

  public readonly providers$ = new BehaviorSubject<Provider[]>([]);
  public readonly providersLoading$ = new BehaviorSubject<boolean>(false);
  public readonly providersError$ = new BehaviorSubject<string | null>(null);

  public readonly partnerProvidersByBpId$ = new BehaviorSubject<Record<number, BusinessPartnerProviderLink[]>>({});

  private initialized = false;
  private authSubscription: Subscription | null = null;
  private activeParentId: number | null = null;
  private providersLoaded = false;
  private partnersLoaded = false;

  constructor(private api: CopilotApiService, private auth: AuthService) {}

  public initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.authSubscription = this.auth.user$.subscribe((me) => {
      if (!me) {
        return;
      }
      const metadata = (me as any)['user_metadata'] as Record<string, unknown> | undefined;
      const parentId = this.normalizeParentId(metadata?.['BusinessPartnerId']);
      this.ensureReferenceData(parentId);
    });
  }

  public refresh(parentId?: number): void {
    const targetParentId = this.normalizeParentId(parentId ?? this.activeParentId ?? 1);
    this.loadPartners(targetParentId, true);
  }

  public refreshPartnerProviderLinks(parentId?: number): void {
    const targetParentId = this.normalizeParentId(parentId ?? this.activeParentId ?? 1);
    this.loadPartners(targetParentId, true);
  }

  public refreshProviders(): void {
    this.loadProviders(true);
  }

  public setPartnerEnabled(partnerId: number, enabled: boolean): void {
    const current = this.partners$.value || [];
    const updated = current.map((p) => (p.Id === partnerId ? { ...p, Enabled: enabled } : p));
    this.partners$.next(updated);
    this.rebuildProviderMaps(updated);
  }

  public getProvidersForBusinessPartner(businessPartnerId: number | null | undefined): Provider[] {
    if (businessPartnerId == null) {
      return [];
    }
    const providerLinks = this.partnerProvidersByBpId$.value[businessPartnerId] || [];
    if (providerLinks.length === 0) {
      return [];
    }

    const providerById = new Map(this.providers$.value.map((provider) => [provider.Id, provider]));
    const resolvedProviders: Provider[] = [];
    for (const link of providerLinks) {
      const fromGlobal = providerById.get(link.ProviderId);
      if (fromGlobal) {
        resolvedProviders.push(fromGlobal);
        continue;
      }
      resolvedProviders.push({ Id: link.ProviderId, Name: link.ProviderName });
    }

    return resolvedProviders;
  }

  private ensureReferenceData(parentId: number): void {
    const shouldReloadPartners = this.activeParentId !== parentId || !this.partnersLoaded;
    this.activeParentId = parentId;

    if (shouldReloadPartners) {
      this.loadPartners(parentId, false);
    }
    if (!this.providersLoaded) {
      this.loadProviders(false);
    }
  }

  private loadPartners(parentId: number, force: boolean): void {
    if (!force && this.partnersLoaded && this.activeParentId === parentId) {
      return;
    }

    this.activeParentId = parentId;
    this.partnersLoading$.next(true);
    this.partnersError$.next(null);

    this.api.getBusinessPartners(parentId, { expand: 'providers' }).subscribe({
      next: (list) => {
        const normalized = (list || []).slice();
        this.partnersLoaded = true;
        this.partners$.next(normalized);
        this.rebuildProviderMaps(normalized);
        this.partnersLoading$.next(false);
      },
      error: () => {
        this.partnersLoaded = false;
        this.partners$.next([]);
        this.rebuildProviderMaps([]);
        this.partnersError$.next('partners_load_failed');
        this.partnersLoading$.next(false);
      }
    });
  }

  private loadProviders(force: boolean): void {
    if (!force && this.providersLoaded) {
      return;
    }

    this.providersLoading$.next(true);
    this.providersError$.next(null);

    this.api.getProviders().subscribe({
      next: (list) => {
        this.providersLoaded = true;
        this.providers$.next((list || []).slice());
        this.providersLoading$.next(false);
      },
      error: () => {
        this.providersLoaded = false;
        this.providers$.next([]);
        this.providersError$.next('providers_load_failed');
        this.providersLoading$.next(false);
      }
    });
  }

  private rebuildProviderMaps(partners: BusinessPartner[]): void {
    const byPartnerId: Record<number, BusinessPartnerProviderLink[]> = {};

    for (const partner of partners) {
      if (partner.Id == null) {
        continue;
      }
      const list = Array.isArray(partner.providers) ? partner.providers : [];
      byPartnerId[partner.Id] = list.slice();
    }

    this.partnerProvidersByBpId$.next(byPartnerId);
  }

  private normalizeParentId(value: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return 1;
    }
    return Math.floor(numeric);
  }
}

